import React, { useState } from 'react';
import { Team, UpdateTeamRequest } from '../utils/types';
import { apiPut, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface EditTeamProps {
  team: Team;
  onTeamUpdated: (updatedTeam: Team) => void;
  onCancel: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
}

const EditTeam: React.FC<EditTeamProps> = ({
  team,
  onTeamUpdated,
  onCancel,
  onError,
  onSuccess
}) => {
  const { token } = useAuth();
  const [updating, setUpdating] = useState(false);
  
  // Form state initialized with current team values
  const [formData, setFormData] = useState({
    name: team.name,
    description: team.description || '',
    looking_for_members: team.looking_for_members,
    desired_skills: [...team.desired_skills]
  });
  
  // Skill input state
  const [skillInput, setSkillInput] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      onError('Not authenticated');
      return;
    }

    if (!formData.name.trim()) {
      onError('Team name is required');
      return;
    }

    setUpdating(true);
    
    try {
      // Create update request with only changed fields
      const updateRequest: UpdateTeamRequest = {};
      
      if (formData.name.trim() !== team.name) {
        updateRequest.name = formData.name.trim();
      }
      
      if (formData.description.trim() !== (team.description || '')) {
        updateRequest.description = formData.description.trim();
      }
      
      if (formData.looking_for_members !== team.looking_for_members) {
        updateRequest.looking_for_members = formData.looking_for_members;
      }
      
      // Compare desired skills arrays
      const currentSkills = [...team.desired_skills].sort();
      const newSkills = [...formData.desired_skills].sort();
      if (JSON.stringify(currentSkills) !== JSON.stringify(newSkills)) {
        updateRequest.desired_skills = formData.desired_skills;
      }
      
      // Only make API call if there are actual changes
      if (Object.keys(updateRequest).length === 0) {
        onSuccess('No changes to save');
        onCancel();
        return;
      }
      
      const updatedTeam = await apiPut<Team>(`/teams/${team.team_id}`, updateRequest, token);
      onTeamUpdated(updatedTeam);
      onSuccess('Team updated successfully!');
      onCancel();
      
    } catch (err) {
      if (err instanceof ApiError) {
        onError(`Failed to update team: ${err.message}`);
      } else {
        onError('Failed to update team');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleSkillKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const addSkill = () => {
    const skill = skillInput.trim();
    if (skill && !formData.desired_skills.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        desired_skills: [...prev.desired_skills, skill]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      desired_skills: prev.desired_skills.filter(skill => skill !== skillToRemove)
    }));
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>✏️ Edit Team Details</h2>
        
        <form onSubmit={handleSubmit}>
          {/* Team Name */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="edit-team-name" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Team Name *
            </label>
            <input
              id="edit-team-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="form-input"
              style={{ width: '100%' }}
              disabled={updating}
              required
              maxLength={100}
            />
          </div>

          {/* Team Description */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="edit-team-description" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Description
            </label>
            <textarea
              id="edit-team-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="form-input"
              style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
              disabled={updating}
              maxLength={1000}
              placeholder="Describe your team or project goals..."
            />
          </div>

          {/* Looking for Members */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.looking_for_members}
                onChange={(e) => setFormData(prev => ({ ...prev, looking_for_members: e.target.checked }))}
                disabled={updating}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontWeight: 'bold' }}>🔍 Looking for more members</span>
            </label>
            <small style={{ color: '#666', fontSize: '12px', marginLeft: '24px' }}>
              This helps other participants find your team
            </small>
          </div>

          {/* Desired Skills */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="edit-team-skills" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Skills Needed
            </label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                id="edit-team-skills"
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={handleSkillKeyPress}
                className="form-input"
                style={{ flex: 1 }}
                placeholder="Type a skill and press Enter..."
                disabled={updating}
                maxLength={50}
              />
              <button
                type="button"
                onClick={addSkill}
                disabled={!skillInput.trim() || updating}
                style={{
                  background: skillInput.trim() ? '#007bff' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: skillInput.trim() && !updating ? 'pointer' : 'not-allowed'
                }}
              >
                Add
              </button>
            </div>
            
            {/* Skills display */}
            {formData.desired_skills.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {formData.desired_skills.map((skill, index) => (
                  <span
                    key={`skill-${skill}-${index}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: '5px 12px',
                      borderRadius: '15px',
                      fontSize: '14px'
                    }}
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      disabled={updating}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        marginLeft: '8px',
                        cursor: updating ? 'not-allowed' : 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <small style={{ color: '#666' }}>
              What skills are you looking for in teammates?
            </small>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={updating}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                cursor: updating ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updating || !formData.name.trim()}
              style={{
                background: updating || !formData.name.trim() ? '#ccc' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                cursor: updating || !formData.name.trim() ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {updating ? '⏳ Updating...' : '💾 Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTeam;
